import { Link } from "@inertiajs/react"
import { BookOpen, Calendar, MapPin, Plus, Save } from "lucide-react"
import { useRef, useState, type ReactNode } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { AdminLayout } from "@/layouts/AdminLayout"
import type {
  CategoryOption,
  PaginationMeta,
  QualityMetrics,
  StudioEventRow,
  StudioGuide,
  StudioGuideItem,
  StudioPoiRow,
  StudioRegion,
  StudioUrls,
} from "@/lib/types"

import { EventsTab } from "./studio/EventsTab"
import { GuideTab, type GuideTabHandle } from "./studio/GuideTab"
import { PoisTab } from "./studio/PoisTab"

interface Props {
  region: StudioRegion
  guide: StudioGuide
  guide_items: StudioGuideItem[]
  pois: { records: StudioPoiRow[]; meta: PaginationMeta }
  events: { records: StudioEventRow[]; meta: PaginationMeta }
  categories: CategoryOption[]
  quality: QualityMetrics
  urls: StudioUrls
}

// ---------------------------------------------------------------------------
// Quality metric helpers
// ---------------------------------------------------------------------------

type QualityLevel = "good" | "warning" | "poor"

function fractionLevel(value: number, total: number): QualityLevel {
  if (total === 0) return "poor"
  const pct = value / total
  if (pct >= 0.8) return "good"
  if (pct >= 0.5) return "warning"
  return "poor"
}

function levelClass(level: QualityLevel): string {
  if (level === "good") return "text-green-600"
  if (level === "warning") return "text-amber-600"
  return "text-muted-foreground"
}

interface MetricChipProps {
  label: string
  display: string
  level: QualityLevel
}

function MetricChip({ label, display, level }: MetricChipProps) {
  return (
    <span className="flex items-center gap-1 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-medium ${levelClass(level)}`}>{display}</span>
    </span>
  )
}

// ---------------------------------------------------------------------------
// StudioHeader
// ---------------------------------------------------------------------------

interface StudioHeaderProps {
  region: StudioRegion
  guide: StudioGuide
  guide_items: StudioGuideItem[]
  pois: { records: StudioPoiRow[]; meta: PaginationMeta }
  events: { records: StudioEventRow[]; meta: PaginationMeta }
  quality: QualityMetrics
  urls: StudioUrls
  activeTab: string
  onSaveGuide: () => void
  onNewPoi: () => void
  onNewEvent: () => void
  guideDirty: boolean
  guideSaving: boolean
}

function StudioHeader({
  region,
  guide,
  guide_items,
  pois,
  events,
  quality,
  urls,
  activeTab,
  onSaveGuide,
  onNewPoi,
  onNewEvent,
  guideDirty,
  guideSaving,
}: StudioHeaderProps) {
  const q = quality

  // Fraction metrics
  const notesLevel = fractionLevel(q.curator_notes.value, q.curator_notes.total)
  const timeLevel = fractionLevel(q.time_context.value, q.time_context.total)
  const photosLevel = fractionLevel(q.photos.value, q.photos.total)

  // Highlights: good if within range, warning if above max, poor if below min
  const hlVal = q.highlights.value
  const hlMin = q.highlights.target_min
  const hlMax = q.highlights.target_max
  const highlightsLevel: QualityLevel =
    hlVal >= hlMin && hlVal <= hlMax ? "good" : hlVal < hlMin ? "poor" : "warning"

  // Stale: 0 = good, >0 = amber
  const staleLevel: QualityLevel = q.stale.value === 0 ? "good" : "warning"

  // Events: good if at or above target_min
  const eventsLevel: QualityLevel =
    q.events.value >= q.events.target_min ? "good" : q.events.value > 0 ? "warning" : "poor"

  return (
    <div className="mb-6">
      {/* Breadcrumb + title row */}
      <div className="mb-4 flex items-start justify-between">
        <div>
          <div className="mb-1 flex items-center gap-2 text-sm text-muted-foreground">
            <Link href={urls.region_show} className="hover:underline">
              {region.name}
            </Link>
            <span>/</span>
            <span>Guide Studio</span>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">{guide.title}</h1>
          <div className="mt-1 flex items-center gap-2">
            <Badge
              variant={guide.status === "active" ? "default" : "outline"}
              className="text-xs capitalize"
            >
              {guide.status}
            </Badge>
          </div>
        </div>

        {/* Tab-specific actions */}
        <div className="flex items-center gap-2 pt-1">
          {activeTab === "guide" && (
            <Button
              size="sm"
              disabled={!guideDirty || guideSaving}
              onClick={onSaveGuide}
            >
              <Save className="mr-1.5 size-4" />
              {guideSaving ? "Saving..." : "Save changes"}
            </Button>
          )}
          {activeTab === "pois" && (
            <Button size="sm" onClick={onNewPoi}>
              <Plus className="mr-1.5 size-4" />
              New POI
            </Button>
          )}
          {activeTab === "events" && (
            <Button size="sm" onClick={onNewEvent}>
              <Plus className="mr-1.5 size-4" />
              New event
            </Button>
          )}
        </div>
      </div>

      {/* Stats + quality strip */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            {/* Counts summary */}
            <span className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{guide_items.length}</span> guide items
              {" · "}
              <span className="font-medium text-foreground">{pois.records.length}</span> POIs
              {" · "}
              <span className="font-medium text-foreground">{events.records.length}</span> events
            </span>

            <span className="hidden h-4 w-px bg-border sm:block" aria-hidden />

            {/* Quality metrics strip */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <MetricChip
                label="Notes"
                display={`${q.curator_notes.value}/${q.curator_notes.total}`}
                level={notesLevel}
              />
              <MetricChip
                label="Time"
                display={`${q.time_context.value}/${q.time_context.total}`}
                level={timeLevel}
              />
              <MetricChip
                label="Photos"
                display={`${q.photos.value}/${q.photos.total}`}
                level={photosLevel}
              />
              <MetricChip
                label="Highlights"
                display={`${hlVal} (${hlMin}–${hlMax})`}
                level={highlightsLevel}
              />
              <MetricChip
                label="Stale"
                display={String(q.stale.value)}
                level={staleLevel}
              />
              <MetricChip
                label="Events"
                display={`${q.events.value}/${q.events.target_min} min`}
                level={eventsLevel}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Studio({
  region,
  guide,
  guide_items,
  pois,
  events,
  categories,
  quality,
  urls,
}: Props) {
  const [activeTab, setActiveTab] = useState("guide")
  const [guideDirty, setGuideDirty] = useState(false)
  const [guideSaving, setGuideSaving] = useState(false)
  const [editingPoi, setEditingPoi] = useState<StudioPoiRow | null | "new">(null)
  const [editingEvent, setEditingEvent] = useState<StudioEventRow | null | "new">(null)
  const guideTabRef = useRef<GuideTabHandle>(null)

  return (
    <div>
      <StudioHeader
        region={region}
        guide={guide}
        guide_items={guide_items}
        pois={pois}
        events={events}
        quality={quality}
        urls={urls}
        activeTab={activeTab}
        onSaveGuide={() => guideTabRef.current?.save()}
        onNewPoi={() => setEditingPoi("new")}
        onNewEvent={() => setEditingEvent("new")}
        guideDirty={guideDirty}
        guideSaving={guideSaving}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="guide">
            <BookOpen className="mr-1.5 size-4" />
            Guide
            {guide_items.length > 0 && (
              <Badge variant="secondary" className="ml-1.5 text-xs">
                {guide_items.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="pois">
            <MapPin className="mr-1.5 size-4" />
            POIs
            {pois.records.length > 0 && (
              <Badge variant="secondary" className="ml-1.5 text-xs">
                {pois.records.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="events">
            <Calendar className="mr-1.5 size-4" />
            Events
            {events.records.length > 0 && (
              <Badge variant="secondary" className="ml-1.5 text-xs">
                {events.records.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <div className="mt-6">
          <TabsContent value="guide">
            <GuideTab
              ref={guideTabRef}
              guide_items={guide_items}
              pois={pois}
              urls={urls}
              quality={quality}
              regionCenter={region.boundary_center ?? null}
              onDirtyChange={setGuideDirty}
              onSavingChange={setGuideSaving}
            />
          </TabsContent>

          <TabsContent value="pois">
            <PoisTab
              pois={pois}
              guide_items={guide_items}
              categories={categories}
              urls={urls}
              editingPoi={editingPoi}
              onEditingPoiChange={setEditingPoi}
            />
          </TabsContent>

          <TabsContent value="events">
            <EventsTab
              events={events}
              pois={pois}
              urls={urls}
              editingEvent={editingEvent}
              onEditingEventChange={setEditingEvent}
            />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  )
}

Studio.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
